import React from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

/**
 * Tier Status Badge Component
 * Displays the completion status of metadata collection tiers
 *
 * @param {Object} tierStatus - Tier status object from API
 * @param {boolean} compact - Whether to show compact view (default: false)
 * @param {boolean} showLabels - Whether to show tier labels (default: true)
 */
const TierStatusBadge = ({ tierStatus, compact = false, showLabels = true }) => {
  if (!tierStatus || !tierStatus.tiers) {
    return null;
  }

  const tiers = tierStatus.tiers;

  // Tier 6 (usage analysis) is intentionally absent: see comment in
  // TierProgressPanel.js. Collector not implemented; badge would
  // always render the "unknown" state.
  const tierInfo = [
    { key: 'tier1_discovery', label: 'T1', name: 'Discovery', color: 'blue' },
    { key: 'tier2_schema', label: 'T2', name: 'Schema', color: 'indigo' },
    { key: 'tier3_changes', label: 'T3', name: 'Changes', color: 'purple' },
    { key: 'tier4_profiling', label: 'T4', name: 'Profiling', color: 'pink' },
    { key: 'tier5_lineage', label: 'T5', name: 'Lineage', color: 'violet' }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'success':
        return CheckCircleSolid;
      case 'running':
      case 'in_progress':
        return ArrowPathIcon;
      case 'failed':
      case 'error':
        return XCircleIcon;
      default:
        return ClockIcon;
    }
  };

  const getStatusColor = (status, baseColor) => {
    switch (status) {
      case 'completed':
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'running':
      case 'in_progress':
        return `text-${baseColor}-600 bg-${baseColor}-100 animate-pulse`;
      case 'failed':
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-400 bg-gray-100';
    }
  };

  if (compact) {
    // Compact view: Show small dots
    return (
      <div className="flex items-center gap-1">
        {tierInfo.map((tier) => {
          const tierData = tiers[tier.key] || {};
          const status = tierData.status || 'pending';
          const StatusIcon = getStatusIcon(status);
          const statusColor = getStatusColor(status, tier.color);

          return (
            <div
              key={tier.key}
              className={`w-6 h-6 rounded-full flex items-center justify-center ${statusColor}`}
              title={`${tier.name}: ${status}`}
            >
              <StatusIcon className="w-4 h-4" />
            </div>
          );
        })}
      </div>
    );
  }

  // Full view: Show tier badges with labels
  return (
    <div className="space-y-2">
      {showLabels && (
        <div className="text-xs font-medium text-gray-700 mb-2">
          Tier Collection Status
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {tierInfo.map((tier) => {
          const tierData = tiers[tier.key] || {};
          const status = tierData.status || 'pending';
          const lastRun = tierData.last_run_at;
          const StatusIcon = getStatusIcon(status);
          const statusColor = getStatusColor(status, tier.color);

          return (
            <div
              key={tier.key}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${statusColor} text-xs font-medium transition-all`}
              title={lastRun ? `Last run: ${new Date(lastRun).toLocaleString()}` : 'Not run yet'}
            >
              <StatusIcon className="w-3.5 h-3.5" />
              <span>{tier.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Tier Completion Summary Component
 * Shows overall tier completion percentage
 */
export const TierCompletionSummary = ({ tierStatus }) => {
  if (!tierStatus || !tierStatus.tiers) {
    return null;
  }

  const tiers = Object.values(tierStatus.tiers);
  const totalTiers = tiers.length;
  const completedTiers = tiers.filter(
    (t) => t.status === 'completed' || t.status === 'success'
  ).length;
  const completionPercentage = totalTiers > 0 ? (completedTiers / totalTiers) * 100 : 0;

  const getCompletionColor = () => {
    if (completionPercentage >= 80) return 'text-green-600 bg-green-100';
    if (completionPercentage >= 50) return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${getCompletionColor()} text-sm font-medium`}>
      <div className="relative w-12 h-12">
        <svg className="transform -rotate-90 w-12 h-12">
          <circle
            cx="24"
            cy="24"
            r="20"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            className="text-gray-300 opacity-30"
          />
          <circle
            cx="24"
            cy="24"
            r="20"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            strokeDasharray={`${completionPercentage * 1.25664} ${125.664 - completionPercentage * 1.25664}`}
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-xs font-bold">
          {Math.round(completionPercentage)}%
        </div>
      </div>
      <div className="text-left">
        <div className="font-semibold">Tier {completedTiers}/{totalTiers}</div>
        <div className="text-xs opacity-75">Complete</div>
      </div>
    </div>
  );
};

/**
 * Metadata Freshness Indicator
 * Shows how fresh the tier data is
 */
export const MetadataFreshnessIndicator = ({ tierStatus }) => {
  if (!tierStatus || !tierStatus.lastExecution) {
    return null;
  }

  const lastExecution = new Date(tierStatus.lastExecution);
  const now = new Date();
  const ageInHours = (now - lastExecution) / (1000 * 60 * 60);

  const getFreshnessInfo = () => {
    if (ageInHours < 1) {
      return { label: 'Just now', color: 'text-green-600 bg-green-50', icon: CheckCircleSolid };
    } else if (ageInHours < 24) {
      return { label: `${Math.round(ageInHours)}h ago`, color: 'text-blue-600 bg-blue-50', icon: ClockIcon };
    } else if (ageInHours < 168) {
      const days = Math.round(ageInHours / 24);
      return { label: `${days}d ago`, color: 'text-yellow-600 bg-yellow-50', icon: ClockIcon };
    } else {
      const weeks = Math.round(ageInHours / 168);
      return { label: `${weeks}w ago`, color: 'text-red-600 bg-red-50', icon: XCircleIcon };
    }
  };

  const { label, color, icon: Icon } = getFreshnessInfo();

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${color} text-xs font-medium`}>
      <Icon className="w-3.5 h-3.5" />
      <span>Updated {label}</span>
    </div>
  );
};

export default TierStatusBadge;
